import { FundingPolicyType } from '@shared';

const stepTemplates: Record<string, Omit<PolicyApplicationStep, 'stepNumber'>> = {
  downloadPolicyIllustration: {
    stepType: 'download-policy-illustration' as const,
    title: 'Download Policy Illustration',
    status: 'not_started' as const,
  },
  preUnderwritingForm: {
    stepType: 'pre-underwriting-form' as const,
    title: 'Complete Pre-Underwriting Form',
    status: 'not_started' as const,
  },
  partnerPreUnderwritingForm: {
    stepType: 'partner-pre-underwriting-form' as const,
    title: 'Complete Partner Pre-Underwriting Form',
    status: 'not_started' as const,
  },
  submitPreUnderwriting: {
    stepType: 'submit-pre-underwriting' as const,
    title: 'Send Pre-Underwriting Document',
    status: 'not_started' as const,
  },
  applicationForm: {
    stepType: 'application-form' as const,
    title: 'Complete Application Form',
    status: 'not_started' as const,
  },
  signApplicationForm: {
    stepType: 'sign-application-form' as const,
    title: 'Collect Required Signatures',
    status: 'not_started' as const,
  },
  preApplicationConsentForm: {
    stepType: 'preapplication-consent-form' as const,
    title: 'Provide Pre-Application Consents',
    status: 'not_started' as const,
  },
  consentForm: {
    stepType: 'consent-form' as const,
    title: 'Provide Required Consents',
    status: 'not_started' as const,
  },
  submitApplication: {
    stepType: 'submit-application' as const,
    title: 'Send Application Form',
    status: 'not_started' as const,
  },
} as const;

// Define step configurations for different policy types
interface PolicyStepConfig {
  isJoint: boolean;
  includePreUnderwriting: boolean;
  additionalSteps?: Array<keyof typeof stepTemplates>;
}

export const policyStepConfigs: Partial<Record<FundingPolicyType, PolicyStepConfig>> = {
  [FundingPolicyType.careMattersTwo]: {
    isJoint: false,
    includePreUnderwriting: true,
  },
  [FundingPolicyType.jointCareMatters]: {
    isJoint: true,
    includePreUnderwriting: true,
  },
  [FundingPolicyType.assetCare]: {
    isJoint: false,
    includePreUnderwriting: false,
  },
  [FundingPolicyType.jointAssetCare]: {
    isJoint: true,
    includePreUnderwriting: false,
  },
  [FundingPolicyType.careScoutCareAssurance]: {
    isJoint: false,
    includePreUnderwriting: false,
  },
  // Add more policy types as needed with their specific configurations
};

function buildPolicyApplicationSteps(config: PolicyStepConfig): PolicyApplicationStep[] {
  const steps: PolicyApplicationStep[] = [];
  let stepNumber = 1;

  // Always add download policy illustration as the first step
  steps.push({
    ...stepTemplates.downloadPolicyIllustration,
    stepNumber: stepNumber++,
  });

  steps.push({
    ...stepTemplates.preApplicationConsentForm,
    stepNumber: stepNumber++,
  });

  // Add pre-underwriting steps if required
  if (config.includePreUnderwriting) {
    steps.push({
      ...stepTemplates.preUnderwritingForm,
      stepNumber: stepNumber++,
    });

    if (config.isJoint) {
      steps.push({
        ...stepTemplates.partnerPreUnderwritingForm,
        stepNumber: stepNumber++,
      });
    }

    steps.push({
      ...stepTemplates.submitPreUnderwriting,
      stepNumber: stepNumber++,
    });
  }

  // Add application steps (always included)
  steps.push({
    ...stepTemplates.applicationForm,
    stepNumber: stepNumber++,
  });

  steps.push({
    ...stepTemplates.signApplicationForm,
    stepNumber: stepNumber++,
  });

  steps.push({
    ...stepTemplates.consentForm,
    stepNumber: stepNumber++,
  });

  steps.push({
    ...stepTemplates.submitApplication,
    stepNumber: stepNumber++,
  });

  // Add any additional steps if specified
  if (config.additionalSteps) {
    config.additionalSteps.forEach(stepKey => {
      steps.push({
        ...stepTemplates[stepKey],
        stepNumber: stepNumber++,
      });
    });
  }

  return steps;
}

export const policyTypeApplicationSteps: Partial<
  Record<FundingPolicyType, PolicyApplicationStep[]>
> = Object.entries(policyStepConfigs).reduce(
  (acc, [policyType, config]) => {
    acc[policyType as unknown as FundingPolicyType] = buildPolicyApplicationSteps(config);
    return acc;
  },
  {} as Partial<Record<FundingPolicyType, PolicyApplicationStep[]>>,
);

export const isPolicyEligibleForApplication = (ltcPolicy: PolicyFundingSource) => {
  return (
    ltcPolicy.policyType &&
    ltcPolicy.policyType in policyTypeApplicationSteps &&
    ltcPolicy.addedViaShopInsurance
  );
};
